import Tabs from 'components/Table/Tabs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTitle from 'components/PageTitle';
import { ContentLayout } from 'layouts/ContentLayout/ContentLayout';
import { GraniteButton } from 'components/V2/Button/GraniteButton';

import { NumericCell } from 'components/Table/Table.styles';
import Searchbar from 'components/Table/SearchBar';
import { useState } from 'react';
import { ServerPaginatedTable } from 'components/Table/ServerPaginatedTable';
import { usePaginatedTable } from 'hooks/usePaginatedTable';
import { useSortedTable } from 'hooks/useSortedTable';
import { EmptySearchResults } from 'screens/QuoteIndex/QuoteIndexPage';
import { ColumnDef, Row } from '@tanstack/react-table';
import { getCustomRoles } from '../utils';
import { PaginableRequest } from 'api/common-schemas';
import { isSystemRole } from './utils';
import RoleTag from './RoleTag';
import { CustomRoleType } from './custom-roles.types';

const CustomRolesIndex = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>('');

  const breadcrumbs = [
    {
      icon: 'home',
      label: 'User management',
      onClick: () => navigate('/users'),
    },
    {
      label: 'Role management',
    },
  ];

  const tabs = useMemo(() => {
    return [
      {
        title: 'Roles',
        onClick: () => navigate(''),
      },
    ];
  }, [navigate]);

  const { sortingQueryParams, sortingState, onSortingChange } = useSortedTable({
    initialSorting: [{ id: 'name', desc: false }],
  });

  const _getRoles = (args: PaginableRequest) =>
    getCustomRoles({
      ...args,
      ...(search && { search }),
      ...sortingQueryParams,
    });

  const { data: tableData, ...paginatedTableProps } = usePaginatedTable(
    _getRoles,
    {
      search,
      sortingQueryParams,
    },
    ['custom-roles-index-table', search, sortingQueryParams],
    {
      refetchOnMount: true,
    },
  );

  const sortedTableData = useMemo(() => {
    if (!tableData) return tableData;

    return [...tableData].sort((a, b) => {
      const aIsSystem = isSystemRole(a.name);
      const bIsSystem = isSystemRole(b.name);

      if (aIsSystem && !bIsSystem) return -1;
      if (!aIsSystem && bIsSystem) return 1;

      return a.name.localeCompare(b.name);
    });
  }, [tableData]);

  const tableHeaders: ColumnDef<CustomRoleType>[] = [
    {
      header: 'Role',
      accessorKey: 'name',
      cell: (row) => (
        <RoleTag name={row.row.original.name} color={row.row.original.color} />
      ),
    },
    {
      header: 'Role Type',
      accessorKey: 'name',
      enableSorting: false,
      cell: (row) => (
        <div>{isSystemRole(row.row.original.name) ? 'Standard' : 'Custom'}</div>
      ),
    },
    {
      header: 'Description',
      accessorKey: 'description',
      enableSorting: false,
    },
    {
      header: 'Total # of members',
      accessorKey: 'user_count',
      cell: (row) => <NumericCell>{row.row.original.user_count}</NumericCell>,
    },
  ];

  const onRowClick = (row: Row<CustomRoleType>) =>
    navigate(`${row.original.id}`);

  return (
    <ContentLayout>
      <div className="mb-12 flex items-center justify-between gap-2">
        <PageTitle title="Role management" breadcrumbs={breadcrumbs} />
        <div className="flex gap-4">
          <GraniteButton
            size="large"
            variant="primary"
            onClick={() => navigate('create')}
          >
            Create role
          </GraniteButton>
        </div>
      </div>
      <Tabs
        className="mb-8"
        variant="medium"
        defaultActiveTab={0}
        tabs={tabs}
      />
      <div className="flex w-full flex-wrap items-start justify-between gap-4">
        <Searchbar
          placeholder="Search by role type"
          onSearch={setSearch}
          onQueryClear={() => {
            setSearch('');
          }}
          clearAllValues={search === ''}
        />
        <div className="mt-6 w-full">
          <ServerPaginatedTable
            data={sortedTableData}
            columns={tableHeaders}
            title="Roles"
            sortingState={sortingState}
            onSortingChange={onSortingChange}
            handleRowClick={onRowClick}
            emptyDataElement={<EmptySearchResults />}
            tableContainerClassName="!grid-cols-[max-content_max-content_1fr_max-content]"
            columnGap="48px"
            {...paginatedTableProps}
          />
        </div>
      </div>
    </ContentLayout>
  );
};

export default CustomRolesIndex;
